'use client';

import type { ProductFile } from '@eliadmin/core/src/api/products/types/product-types';
import { useEffect, useRef } from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Phones: fixed 62 px squares (Figma 60459:102723). From `md` the five of the Figma row share the
 * width; with more images the sixth peeks out to invite scrolling.
 */
const SLOT = {
  fit: 'md:basis-[calc((100%-4rem)/5)]',
  scroll: 'md:basis-[calc((100%-4rem)/5.5)]',
} as const;
const FIT_COUNT = 5;

type GalleryThumbnailsProps = {
  images: ProductFile[];
  selected: number;
  onSelect: (index: number) => void;
};

/**
 * Figma „Thumbnails“: a square per image, 1 px border/subtle (2 px border/brand when selected),
 * radius 18 (14 on mobile). The row scrolls sideways and follows the selected image.
 */
export function GalleryThumbnails({ images, selected, onSelect }: GalleryThumbnailsProps) {
  const { t } = useTranslations();
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const thumb = list?.children[selected];
    if (!list || !(thumb instanceof HTMLElement) || list.scrollWidth <= list.clientWidth) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({
      left: thumb.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [selected]);

  return (
    <ul ref={listRef} className="hide-scroll-bar relative flex gap-2 overflow-x-auto md:gap-4">
      {images.map((image, index) => {
        const active = index === selected;
        return (
          <li
            key={`${image.id}-${index}`}
            className={cn(
              'aspect-square shrink-0 basis-[62px]',
              images.length > FIT_COUNT ? SLOT.scroll : SLOT.fit,
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(index)}
              aria-label={`${t('text3532')} ${index + 1}`}
              aria-current={active ? 'true' : undefined}
              className={cn(
                'relative block size-full overflow-hidden rounded-[14px] border bg-white transition-colors md:rounded-[18px]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2',
                active
                  ? 'border-[#D7191F] ring-1 ring-inset ring-[#D7191F]'
                  : 'border-[#E2E4E7] hover:border-[#9096A0]',
              )}
            >
              <Image
                src={image.url}
                alt=""
                fill
                sizes="(min-width: 768px) 120px, 62px"
                className="object-contain md:p-2"
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
